import React, { useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '../../hooks/useTranslation';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { authenticatedFetch } from '../../utils/authenticatedFetch';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { ModernProductSelector } from './ModernProductSelector';

interface Product {
  id: string | number;
  name: string;
  price: number;
  reference?: string;
}

interface OrderItem {
  productId: string | number;
  quantity: number;
  price: number;
  note?: string;
}

// Product cache to avoid repeated API calls
const productCache = new Map();

const ProductNameCell = ({ productId }: { productId: string | number }) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(!productCache.has(productId));
  const [product, setProduct] = useState(productCache.get(productId));
  const [error, setError] = useState<Error | null>(null);

  React.useEffect(() => {
    if (!productId) return;

    // If we have it in cache, use it
    if (productCache.has(productId)) {
      setProduct(productCache.get(productId));
      setLoading(false);
      return;
    }

    // Otherwise fetch it
    setLoading(true);
    setError(null);

    // Use authenticatedFetch instead of manual localStorage
    authenticatedFetch(`/api/products/${productId}`)
      .then((response) => response.json())
      .then((productData) => {
        // Store in cache
        productCache.set(productId, productData);
        setProduct(productData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading product:', err);
        setError(err);
        setLoading(false);
      });
  }, [productId]);

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-pulse bg-muted h-4 w-20 rounded"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <span className="text-destructive text-sm">
        {translate('pos.common.notification.item_doesnt_exist')}
      </span>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <div className="font-medium text-sm truncate">{product.name}</div>
      {product.reference && (
        <div className="text-xs text-muted-foreground truncate">
          {product.reference}
        </div>
      )}
    </div>
  );
};

interface ModernItemsGridProps {
  disabled?: boolean;
  allowOnlyAddItems?: boolean; // If true, only allow adding new items, not editing existing ones
  originalItemsCount?: number; // Number of items that were originally in the order (for suborders)
}

export const ModernItemsGrid: React.FC<ModernItemsGridProps> = ({
  disabled = false,
  allowOnlyAddItems = false,
  originalItemsCount,
}) => {
  const { translate } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [showProductSelector, setShowProductSelector] = useState(false);

  console.log(`ModernItemsGrid rendered. Disabled: ${disabled}`);

  const items: OrderItem[] = watch('items') || [];

  const addProduct = useCallback(
    (product: Product) => {
      const existingItemIndex = items.findIndex(
        (item) => item.productId === product.id
      );

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        const updatedItems = [...items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + 1,
        };
        setValue('items', updatedItems, { shouldDirty: true });
      } else {
        // Add new item
        const newItem: OrderItem = {
          productId: product.id,
          quantity: 1,
          price: product.price,
          note: '',
        };
        setValue('items', [...items, newItem], { shouldDirty: true });
      }
    },
    [items, setValue]
  );

  const updateQuantity = useCallback(
    (index: number, newQuantity: number) => {
      if (newQuantity <= 0) {
        // Remove item
        const updatedItems = items.filter((_, i) => i !== index);
        setValue('items', updatedItems, { shouldDirty: true });
      } else {
        // Update quantity
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], quantity: newQuantity };
        setValue('items', updatedItems, { shouldDirty: true });
      }
    },
    [items, setValue]
  );

  const updateNote = useCallback(
    (index: number, note: string) => {
      const updatedItems = [...items];
      updatedItems[index] = { ...updatedItems[index], note };
      setValue('items', updatedItems, { shouldDirty: true });
    },
    [items, setValue]
  );

  const removeItem = useCallback(
    (index: number) => {
      const updatedItems = items.filter((_, i) => i !== index);
      setValue('items', updatedItems, { shouldDirty: true });
    },
    [items, setValue]
  );

  const reduceProductQuantity = useCallback(
    (productId: string | number) => {
      const existingItemIndex = items.findIndex(
        (item) => item.productId === productId
      );

      if (existingItemIndex >= 0) {
        const currentQuantity = items[existingItemIndex].quantity;
        if (currentQuantity > 1) {
          // Reduce quantity by 1
          const updatedItems = [...items];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: currentQuantity - 1,
          };
          setValue('items', updatedItems, { shouldDirty: true });
        } else {
          // Remove item if quantity would be 0
          removeItem(existingItemIndex);
        }
      }
    },
    [items, setValue, removeItem]
  );

  const totalAmount = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {translate('pos.orders.fields.items')}
            </CardTitle>
            <Button
              data-testid="add-product-button"
              onClick={() => {
                console.log('🔘 ADD PRODUCT BUTTON CLICKED!');
                setShowProductSelector(true);
              }}
              size="sm"
              disabled={disabled}
            >
              {translate('pos.orders.actions.addProduct')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {disabled && (
            <div className="text-center py-4 bg-muted/50 border-b">
              <p className="text-sm text-muted-foreground">
                {translate('pos.orders.messages.orderCannotBeModified')}
              </p>
            </div>
          )}
          {items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {translate('pos.orders.messages.selectProducts')}
            </div>
          ) : (
            <div className="space-y-2 p-4">
              {items.map((item, index) => (
                <div key={`${item.productId}-${index}`} className="space-y-2">
                  {/* Item Row */}
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <ProductNameCell productId={item.productId} />
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          <span data-sensitive-field="amount">
                            {formatCurrency(item.price)}
                          </span>
                        </Badge>
                      </div>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        className="h-8 w-8 p-0"
                        disabled={disabled}
                      >
                        -
                      </Button>
                      <span className="w-12 text-center font-medium">
                        {item.quantity}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="h-8 w-8 p-0"
                        disabled={disabled}
                      >
                        +
                      </Button>
                    </div>

                    {/* Total */}
                    <div className="text-right min-w-0">
                      <div className="font-medium">
                        <span data-sensitive-field="amount">
                          {formatCurrency(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeItem(index)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive/80"
                      disabled={disabled}
                    >
                      ×
                    </Button>
                  </div>

                  {/* Note Input - Right after item line */}
                  <div className="pl-3 pr-3 pb-2">
                    <Input
                      type="text"
                      placeholder={translate('pos.orders.fields.note')}
                      value={item.note || ''}
                      onChange={(e) => updateNote(index, e.target.value)}
                      className="w-full"
                      disabled={disabled}
                    />
                  </div>
                </div>
              ))}

              {/* Total */}
              <Separator />
              <div className="flex justify-between items-center pt-2">
                <span className="font-medium">
                  {translate('pos.orders.fields.total')}:
                </span>
                <span className="text-lg font-bold">
                  <span data-sensitive-field="amount">
                    {formatCurrency(totalAmount)}
                  </span>
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showProductSelector && (
        <ModernProductSelector
          onAddProduct={addProduct}
          onReduceProduct={reduceProductQuantity}
          onClose={() => setShowProductSelector(false)}
          currentItems={items}
        />
      )}
    </>
  );
};
